import { Button, Divider } from '@otter-ai/den-components';
import { SalesforceIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { useExperimentEnabled } from '@/experiment/experiments-client';
import { FormattedText } from '@/shared/components/FormattedText';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import { BUILT_IN_APPS } from '@/shared/types/apps';
import { descriptionFormatRules } from '../../shared/utils/text';
import { useCrmPaywall } from '../hooks/useCrmPaywall';
import { useSalesforceOAuth } from '../hooks/useSalesforceOAuth';
import { useSalesforceSettings } from '../hooks/useSalesforceSettings';
import { useSyncedAccounts } from '../hooks/useSyncedAccounts';
import { SALESFORCE_TEST_IDS } from '../test-ids/salesforce';
import { trackCRM } from '../tracking/crm-tracking';
import type { SalesforceSyncedAccount } from '../type/apps';
import { AppRowLayout } from './AppRowLayout';
import { CrmLimitPaywall } from './CrmLimitPaywall';
import { CrmUpgradeButton } from './CrmUpgradeButton';
import { DisconnectedButton } from './DisconnectedButton';

export function SalesforceAppRow() {
  const t = useTranslations('settings:integrations.apps.salesforce');
  const { adminStatus } = useWorkspace();
  const {
    canManageTeamSalesforce,
    canManageMemberSalesforce,
    openSalesforceMemberManageModal,
    openSalesforceTeamManageModal,
    canConnectSalesforceAccount,
    canAddAllSalesforceAccounts,
    disableDisconnectSalesforce,
    isConnectToSalesforce,
    canShowButtons,
  } = useSalesforceSettings();
  const {
    openSalesforceAuthorizationPage,
    disconnectSalesforce,
    connectAllSalesforceAccounts,
  } = useSalesforceOAuth();

  const isSalesforceFeaturesConfigEnabled = useExperimentEnabled(
    'salesforce_features_config',
  );

  const isSalesforceAutofillConfigEnabled = useExperimentEnabled(
    'salesforce_autofill_config_fe',
  );

  const paywallInfo = useCrmPaywall('Salesforce');

  const handleDisconnect = useCallback(async () => {
    trackCRM.main.clickDisconnect('salesforce');
    const confirmed = await openConfirmDialog({
      title: t('disconnectModal.title'),
      message: (
        <FormattedText>
          {t.rich('disconnectModal.description', descriptionFormatRules())}
        </FormattedText>
      ),
      confirmText: t('disconnectModal.confirmText'),
      cancelText: t('disconnectModal.cancelText'),
      danger: true,
    });
    if (!confirmed) {
      return true;
    }

    try {
      await disconnectSalesforce();
      trackCRM.main.disconnected('salesforce');
      // TODO(sleahy): Add success toast
      return true;
    } catch {
      trackCRM.main.disconnectedFailed('salesforce');
      // TODO(sleahy): Add error toast
      return false;
    }
  }, [disconnectSalesforce]);

  const extendContent = isSalesforceFeaturesConfigEnabled && (
    <SalesforceExtendContent
      canAddAllSalesforceAccounts={canAddAllSalesforceAccounts ?? false}
      connectAllSalesforceAccounts={connectAllSalesforceAccounts}
    />
  );

  // Show paywall: always when not connected, or only to admins when connected
  const shouldShowPaywallBanner =
    paywallInfo.isCrmPaywallEnabled &&
    paywallInfo.shouldShowPaywall &&
    (!isConnectToSalesforce || adminStatus?.isAdmin);

  const paywallContent = shouldShowPaywallBanner && (
    <CrmLimitPaywall
      service="salesforce"
      messageKey={paywallInfo.paywallMessageKey}
      connectionsRemaining={paywallInfo.connectionsRemaining}
      showUpgradeButton={paywallInfo.showUpgradeButton}
      showContactSalesButton={paywallInfo.showContactSalesButton}
      connectionsUsage={paywallInfo.connectionsUsage}
      limitReached={paywallInfo.isCrmLimitReached}
    />
  );
  return (
    <div data-tutorial="self-serve-crm">
      <AppRowLayout
        appRowId="salesforce-app"
        Icon={SalesforceIcon}
        tags={{
          enterpriseTag: !paywallInfo.isCrmPaywallEnabled,
          proTag: paywallInfo.isCrmPaywallEnabled && paywallInfo.isFreePlan,
        }}
        title="Salesforce"
        description={<SalesforceDescription />}
        buttons={
          canShowButtons && (
            <div className="flex gap-2 flex-col">
              {/* Disconnect button */}
              {isConnectToSalesforce && !disableDisconnectSalesforce && (
                <DisconnectedButton
                  disconnectButtonText={t('disconnectButton')}
                  disconnectAction={handleDisconnect}
                  tooltipCaption={t('disconnectTooltip')}
                  data-testid={SALESFORCE_TEST_IDS.disconnectButton}
                />
              )}
              {/* Upgrade button for free users in CRM experiment */}
              {!canConnectSalesforceAccount &&
                paywallInfo.isCrmPaywallEnabled &&
                paywallInfo.isFreePlan && (
                  <CrmUpgradeButton
                    service="salesforce"
                    connectionsUsage={paywallInfo.connectionsUsage}
                    limitReached={paywallInfo.isCrmLimitReached}
                  />
                )}
              {/* Schedule demo button */}
              {!canConnectSalesforceAccount &&
                isSalesforceFeaturesConfigEnabled && (
                  <Button
                    onClick={() => {
                      trackCRM.main.clickScheduleDemo();
                      window.open(t('scheduleUrl'), '_blank');
                    }}
                  >
                    {t('scheduleDemoButton')}
                  </Button>
                )}

              {/* Add button */}
              {canConnectSalesforceAccount && !isConnectToSalesforce && (
                <Button
                  onClick={() => {
                    trackCRM.main.clickConnect('salesforce');
                    openSalesforceAuthorizationPage({
                      connectionType: 'member',
                    });
                  }}
                  data-testid={SALESFORCE_TEST_IDS.connectButton}
                  disabled={paywallInfo.isCrmLimitReached}
                >
                  {t('addButton')}
                </Button>
              )}
              {/* Connect all button (Old) */}
              {canAddAllSalesforceAccounts &&
                !isSalesforceFeaturesConfigEnabled && (
                  <Button
                    onClick={() => {
                      trackCRM.main.clickConnectAll('salesforce');
                      connectAllSalesforceAccounts();
                    }}
                  >
                    {t('connectAllButton')}
                  </Button>
                )}
              {/* Manage button for admin (New) */}
              {(canManageTeamSalesforce || canAddAllSalesforceAccounts) &&
                isSalesforceFeaturesConfigEnabled && (
                  <Button
                    onClick={() => {
                      trackCRM.main.clickManageFields('salesforce');
                      openSalesforceTeamManageModal();
                    }}
                    data-testid={SALESFORCE_TEST_IDS.manageButton}
                  >
                    {t('manageButton')}
                  </Button>
                )}
              {/* Manage button for admin (Old) */}
              {isSalesforceAutofillConfigEnabled &&
                canManageTeamSalesforce &&
                !isSalesforceFeaturesConfigEnabled && (
                  <Button
                    onClick={() => {
                      trackCRM.main.clickManageFields('salesforce');
                      openSalesforceTeamManageModal();
                    }}
                    data-testid={SALESFORCE_TEST_IDS.manageButton}
                  >
                    {t('manageButton')}
                  </Button>
                )}
              {/* Manage button for member */}
              {canManageMemberSalesforce &&
                isSalesforceFeaturesConfigEnabled && (
                  <Button
                    onClick={() => {
                      trackCRM.main.clickManageFields('salesforce');
                      openSalesforceMemberManageModal();
                    }}
                    data-testid={SALESFORCE_TEST_IDS.manageButton}
                  >
                    {t('manageButton')}
                  </Button>
                )}
            </div>
          )
        }
        extendContent={extendContent}
        fullWidthContent={paywallContent}
      />
    </div>
  );
}

function SalesforceDescription() {
  const { accounts, isLoading: isLoadingAccounts } = useSyncedAccounts({
    service: BUILT_IN_APPS.SALESFORCE,
  });
  const { isLoading: isLoadingWorkspace, adminStatus } = useWorkspace();
  const salesforceAccount = accounts?.[0] as
    | SalesforceSyncedAccount
    | undefined;
  const { canManageTeamSalesforce, canAddAllSalesforceAccounts } =
    useSalesforceSettings();
  const isSalesforceFeaturesConfigEnabled = useExperimentEnabled(
    'salesforce_features_config',
  );
  const isSalesforceAutofillConfigEnabled = useExperimentEnabled(
    'salesforce_autofill_config_fe',
  );

  if (isLoadingAccounts || isLoadingWorkspace) {
    return null;
  }

  if (!isSalesforceFeaturesConfigEnabled && !salesforceAccount) {
    return <OldSalesforceBeforeConnectDescription />;
  }
  if (isSalesforceFeaturesConfigEnabled && !salesforceAccount) {
    return <SalesforceBeforeConnectDescription />;
  }

  return (
    <>
      {/* TODO(sleahy): Move to `AppRowLayout`'s `linkedAccounts` prop */}
      <SalesforceConnectedAccountDescription
        salesforceAccount={salesforceAccount as SalesforceSyncedAccount}
      />
      {!isSalesforceFeaturesConfigEnabled && (
        <OldSalesforceAdminConnectAllDescription
          canAddAllSalesforceAccounts={canAddAllSalesforceAccounts ?? false}
        />
      )}
      {isSalesforceFeaturesConfigEnabled ? (
        <SalesforceAdminManageDescription
          canManageTeamSalesforce={canManageTeamSalesforce ?? false}
        />
      ) : (
        <OldSalesforceAdminManageDescription
          canManageTeamSalesforce={
            (isSalesforceAutofillConfigEnabled && canManageTeamSalesforce) ??
            false
          }
        />
      )}
      {isSalesforceFeaturesConfigEnabled ? (
        <SalesforceIndividualUserConnectedDescription
          salesforceAccount={salesforceAccount as SalesforceSyncedAccount}
          isAdmin={adminStatus?.isAdmin ?? false}
        />
      ) : (
        <OldSalesforceIndividualUserConnectedDescription
          salesforceAccount={salesforceAccount as SalesforceSyncedAccount}
          isAdmin={adminStatus?.isAdmin ?? false}
        />
      )}
    </>
  );
}

function SalesforceExtendContent({
  canAddAllSalesforceAccounts,
  connectAllSalesforceAccounts,
}: {
  canAddAllSalesforceAccounts: boolean;
  connectAllSalesforceAccounts: () => void;
}) {
  const t = useTranslations('settings:integrations.apps.salesforce');

  if (!canAddAllSalesforceAccounts) {
    return null;
  }

  return (
    <>
      <Divider orientation="horizontal" className="my-4" />
      <div className="flex flex-row gap-2 justify-between items-center">
        <SalesforceAdminConnectAllDescription
          canAddAllSalesforceAccounts={canAddAllSalesforceAccounts}
        />
        <Button
          onClick={() => {
            trackCRM.main.clickConnectAll('salesforce');
            connectAllSalesforceAccounts();
          }}
        >
          {t('addButton')}
        </Button>
      </div>
    </>
  );
}

function OldSalesforceBeforeConnectDescription() {
  const t = useTranslations('settings:integrations.apps.salesforce');
  return (
    <FormattedText>
      {t.rich(
        'oldDescriptionBeforeConnect',
        descriptionFormatRules({
          links: {
            'schedule-demo-link': {
              href: t('scheduleUrl'),
              target: '_blank',
            },
          },
        }),
      )}
    </FormattedText>
  );
}

function SalesforceBeforeConnectDescription() {
  const t = useTranslations('settings:integrations.apps.salesforce');
  return (
    <FormattedText>
      {t.rich(
        'descriptionBeforeConnect',
        descriptionFormatRules({
          links: {
            'installation-guide-link': {
              href: t('installationGuideUrl'),
              target: '_blank',
            },
          },
        }),
      )}
    </FormattedText>
  );
}

function SalesforceConnectedAccountDescription({
  salesforceAccount,
}: {
  salesforceAccount: SalesforceSyncedAccount;
}) {
  const t = useTranslations('settings:integrations.apps.salesforce');
  if (!salesforceAccount.creator_email) {
    return null;
  }

  // Get instance_short_id from instance_url
  const instanceShortId = salesforceAccount.instance_url
    ? getInstanceShortId(salesforceAccount.instance_url)
    : null;

  return (
    <FormattedText>
      {t.rich('connectedSalesforceAccount', {
        'email-link': (chunk) => (
          <a href={`mailto:${salesforceAccount.creator_email}`}>{chunk}</a>
        ),
        creatorEmail: salesforceAccount.creator_email,
        'instance-link': (chunk) => (
          <a href={salesforceAccount.instance_url} target="_blank">
            {chunk}
          </a>
        ),
        instanceName: instanceShortId ?? '',
      })}
    </FormattedText>
  );
}

function OldSalesforceAdminConnectAllDescription({
  canAddAllSalesforceAccounts,
}: {
  canAddAllSalesforceAccounts: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.salesforce');
  if (!canAddAllSalesforceAccounts) {
    return null;
  }
  return (
    <FormattedText>
      {t.rich(
        'oldDescriptionAdminConnectAll',
        descriptionFormatRules({
          links: {
            'package-link': {
              href: t('packageUrl'),
              target: '_blank',
            },
            'installation-guide-link': {
              href: t('installationGuideUrl'),
              target: '_blank',
            },
          },
        }),
      )}
    </FormattedText>
  );
}

function SalesforceAdminConnectAllDescription({
  canAddAllSalesforceAccounts,
}: {
  canAddAllSalesforceAccounts: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.salesforce');
  const isSalesforceAutofillConfigEnabled = useExperimentEnabled(
    'salesforce_autofill_config_fe',
  );

  // Use settings hook to know if the "connect all" feature should be shown
  if (!canAddAllSalesforceAccounts) {
    return null;
  }
  return (
    <div className="text-sm text-subtle">
      <FormattedText>
        {t.rich('descriptionAdminConnectAll', {
          ...descriptionFormatRules({
            links: {
              'package-link': {
                href: t('packageUrl'),
                target: '_blank',
              },
              'installation-guide-link': {
                href: t('installationGuideUrl'),
                target: '_blank',
              },
            },
          }),
          li: (() => {
            let index = 0;
            return (children) => {
              index += 1;
              // Hide the 2nd bullet ("Customize field mappings...") when autofill config is disabled
              if (!isSalesforceAutofillConfigEnabled && index === 2) {
                return null;
              }
              return <li>{children}</li>;
            };
          })(),
        })}
      </FormattedText>
    </div>
  );
}

function OldSalesforceAdminManageDescription({
  canManageTeamSalesforce,
}: {
  canManageTeamSalesforce: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.salesforce');

  if (!canManageTeamSalesforce) {
    return null;
  }
  return (
    <FormattedText>
      {t.rich('oldDescriptionAdminManage', descriptionFormatRules())}
    </FormattedText>
  );
}

function SalesforceAdminManageDescription({
  canManageTeamSalesforce,
}: {
  canManageTeamSalesforce: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.salesforce');
  // Experiment flag to determine if the "Enrich Otter AI Chat" bullet should be shown
  const isCrmEnrichedMccEnabled = useExperimentEnabled('crm_enriched_mcc');
  const isSalesforceAutofillConfigEnabled = useExperimentEnabled(
    'salesforce_autofill_config_fe',
  );
  if (!canManageTeamSalesforce) {
    return null;
  }
  return (
    <FormattedText>
      {t.rich(
        'descriptionAdminManage',
        // Override the default <li> formatter so we can hide the last bullet
        {
          ...descriptionFormatRules(),
          li: (() => {
            let index = 0;
            return (children) => {
              index += 1;
              // Hide the 2nd bullet ("Customize field mappings...") when autofill config is disabled
              if (!isSalesforceAutofillConfigEnabled && index === 2) {
                return null;
              }
              // Hide the 3rd bullet ("Enrich Otter AI Chat ...") when the experiment is disabled
              if (!isCrmEnrichedMccEnabled && index === 3) {
                return null;
              }
              return <li>{children}</li>;
            };
          })(),
        },
      )}
    </FormattedText>
  );
}

function OldSalesforceIndividualUserConnectedDescription({
  salesforceAccount,
  isAdmin,
}: {
  salesforceAccount: SalesforceSyncedAccount;
  isAdmin: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.salesforce');
  if (salesforceAccount?.is_member_link || isAdmin) {
    return null;
  }
  return (
    <FormattedText>
      {t.rich(
        'oldDescriptionAdminConnectedIndividual',
        descriptionFormatRules(),
      )}
    </FormattedText>
  );
}

function SalesforceIndividualUserConnectedDescription({
  salesforceAccount,
  isAdmin,
}: {
  salesforceAccount: SalesforceSyncedAccount;
  isAdmin: boolean;
}) {
  const t = useTranslations('settings:integrations.apps.salesforce');
  const isCrmEnrichedMccEnabled = useExperimentEnabled('crm_enriched_mcc');
  const isSalesforceAutofillConfigEnabled = useExperimentEnabled(
    'salesforce_autofill_config_fe',
  );
  if (salesforceAccount?.is_member_link || isAdmin) {
    return null;
  }
  return (
    <FormattedText>
      {t.rich('descriptionAdminManage', {
        ...descriptionFormatRules(),
        li: (() => {
          let index = 0;
          return (children) => {
            index += 1;
            // Hide the 2nd bullet ("Customize field mappings...") when autofill config is disabled
            if (!isSalesforceAutofillConfigEnabled && index === 2) {
              return null;
            }
            if (!isCrmEnrichedMccEnabled && index === 3) {
              return null;
            }
            return <li>{children}</li>;
          };
        })(),
      })}
    </FormattedText>
  );
}

// Helper function to get instance_short_id from instance_url
function getInstanceShortId(instanceUrl: string): string {
  try {
    const url = new URL(instanceUrl);
    return url.hostname.replace(/my\.salesforce\.com$/, '');
  } catch {
    return 'Salesforce';
  }
}
